<script lang="ts">
  // components
  import { widont } from 'journalize';
  import Results from './Results.svelte';
  import Props from './Props.svelte';

  // packages
  import { derived } from 'svelte/store';

  // utils
  import { slugify } from '../utils/slugify.js';
  import { raceNotes } from '../utils/race-helpers.js';

  // types
  import type { Readable } from 'svelte/store';
  import type { Race } from '../types';

  /** The filtered races for one embed (from mountRaceList); updates when the feed polls */
  export let races: Readable<Race[]>;
  /** How each race is drawn */
  export let component: 'result' | 'proposition';
  /** Custom captions keyed by race id; races not listed use captionField */
  export let captions: Record<string, string> = {};
  /** Grey notes under the caption, keyed by race id (e.g. how a race is won) */
  export let captionNote: Record<string, string> = {};
  /** Which feed field the caption shows: 'title' ('Board of Equalization — Seat 1') or 'jurisdiction' ('Seat 1') */
  export let captionField: 'title' | 'jurisdiction' = 'title';
  /** false hides each race's caption (e.g. a story-page embed under a headline) */
  export let showCaption = true;
  /**
   * Adds a dropdown that filters the list by city or district (each race's
   * `jurisdiction`), like the primary's cities and schools embeds. The word
   * after "Show all", e.g. 'cities'. Leave as '' for no dropdown.
   */
  export let dropdown = '';

  // Races keyed by id, built once each time the feed polls, so each race's
  // store is a lookup instead of a search through the whole list
  const racesById = derived(
    races,
    ($races) => new Map($races.map((race) => [race.id, race]))
  );

  // Results and Props each take a store of ONE race. Keep one
  // store per race id so the same store is reused when the feed polls.
  const raceStores = new Map<string, Readable<Race | undefined>>();
  function raceStore(id: string) {
    if (!raceStores.has(id)) {
      raceStores.set(
        id,
        derived(racesById, ($racesById) => $racesById.get(id))
      );
    }
    return raceStores.get(id)!;
  }

  $: multiple = $races.length > 1;

  // DROPDOWN
  // The city or district picked in the dropdown; '' means "Show all"
  let selectedJurisdiction = '';

  // Dropdown options: each city or district once, in the order they appear
  $: jurisdictionOptions = [
    ...new Set($races.map((race) => race.attributes.jurisdiction)),
  ].filter(Boolean);

  // The races to draw. Races from other cities or districts aren't drawn at
  // all while one is picked, so there's nothing to hide.
  $: racesToShow =
    selectedJurisdiction === ''
      ? $races
      : $races.filter(
          (race) => race.attributes.jurisdiction === selectedJurisdiction
        );
</script>

{#if dropdown}
  <!-- Same markup and classes as the primary's dropdown macro
       (_layouts/macros/dropdown.html) -->
  <div class="mb-2">
    <div class="dropdown-container">
      <select
        aria-label="Select an option to filter results by"
        class="dropdown {slugify(dropdown)}"
        bind:value={selectedJurisdiction}
      >
        <option value="">Show all {dropdown}</option>
        {#each jurisdictionOptions as jurisdiction}
          <option value={jurisdiction}>{jurisdiction}</option>
        {/each}
      </select>
    </div>
  </div>
{/if}

<div
  class="race-embed-container"
  class:display-grid={multiple}
  class:grid-cols-2={multiple}
  class:grid-gap-3={multiple}
  data-type={component}
>
  {#each racesToShow as race (race.id)}
    <div
      class="race-embed-container__race"
      class:result-max={!multiple}
      class:m-0={multiple}
    >
      <!-- Same header as the primary's race_template.html: caption, then
           description, pass-rate and vote-for notes (see raceNotes) and
           captionNote when they have text -->
      {#if showCaption}
        <p
          class="contest__caption text-left text-sans-serif"
          class:tight-margins={race.attributes.description ||
            raceNotes(race).length}
          class:footnote={raceNotes(race).length &&
            !race.attributes.description}
        >
          {widont(captions[race.id] ?? race.attributes[captionField])}
        </p>
      {/if}
      {#if race.attributes.description}
        <p class="contest__description text-left text-sans-serif">
          {race.attributes.description}
        </p>
      {/if}
      {#each raceNotes(race) as note}
        <p
          class="footnote text-right text-xs text-regular grid-align-end text-sans-serif"
        >
          {note}
        </p>
      {/each}
      {#if captionNote[race.id]}
        <p class="contest__caption text-xs text-regular color-grey">
          {captionNote[race.id]}
        </p>
      {/if}

      {#if component === 'result'}
        <Results feed={raceStore(race.id)} />
      {:else if component === 'proposition'}
        <Props feed={raceStore(race.id)} />
      {/if}
    </div>
  {/each}
</div>
